/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useCheckInDesk
 * --------------
 * What the one global check-in scanner needs before the first scan: the
 * organizer's events (the same cached query the Events page uses), which of
 * them are open for check-in right now, their activities, and how many people
 * have checked in across them.
 *
 * The clock ticks once a minute, so an event opens on the desk at start − 60
 * min and drops off overnight without a reload. The clock makes no requests.
 *
 * NO BACKGROUND REQUESTS. Counts load once when the desk opens, the All scans
 * list when it is shown; after that both change only when this desk saves a
 * scan (updated from the save itself, no refetch) or the organizer taps
 * Refresh. Scans made on other phones appear after a Refresh.
 *
 * NOTHING IS READ IN FULL. Lists come 50 passes a page ("Show more" loads the
 * next), and every total is the endpoint's COUNT(*) — a one-row read.
 */

import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import useOrganizerOrg from './useOrganizerOrg';
import useOrganizerEvents from './useOrganizerEvents';
import { openEvents, ownEvents } from '../components/organizer/checkin/checkInRules';
import {
  PASS_STATUSES, CHECK_IN_TABLE, CHECK_OUT_TABLE, countChecks, fetchActivities, fetchPassPage, fetchPassTimes,
} from '../components/organizer/checkin/checkInApi';

export const checkInApi = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** `new Date()`, refreshed every minute. */
export function useMinuteClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/**
 * `value`, once it has stopped changing for `delay` ms — so typing a search
 * makes one request, not one per key. Clearing it applies at once.
 */
export function useDebounced(value, delay = 350) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return value === '' ? value : settled;
}

/**
 * The organizer's own events (see ownEvents) and which are open for check-in
 * right now, through the same cached query the Events page uses.
 */
export function useOpenCheckInEvents() {
  const organization = useOrganizerOrg();
  const query = useOrganizerEvents(organization.organizationId);
  const { isLoading, isError, refetch } = query;
  const events = useMemo(
    () => ownEvents(query.events, organization.organizationId),
    [query.events, organization.organizationId]
  );
  const now = useMinuteClock();
  const open = useMemo(() => openEvents(events, now), [events, now]);
  return {
    now,
    events,
    open,
    isLoading: organization.isLoading || isLoading,
    isError: organization.isError || isError,
    refetch: () => { organization.refetch?.(); refetch(); },
  };
}

export default function useCheckInDesk() {
  const queryClient = useQueryClient();
  const desk = useOpenCheckInEvents();
  const openIds = useMemo(() => desk.open.map((event) => event.id), [desk.open]);
  const idsKey = openIds.join(',');

  const activities = useQuery({
    queryKey: ['checkInActivities', idsKey],
    queryFn: () => fetchActivities(checkInApi, openIds),
    enabled: openIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Everyone checked in across the open events: one COUNT(*), however many are open.
  const total = useQuery({
    queryKey: ['checkInTotal', idsKey],
    queryFn: () => countChecks(checkInApi, CHECK_IN_TABLE, openIds),
    enabled: openIds.length > 0,
    staleTime: Infinity,
  });

  const eventsById = useMemo(
    () => Object.fromEntries(desk.events.map((event) => [event.id, event])),
    [desk.events]
  );
  const activitiesById = useMemo(
    () => Object.fromEntries((activities.data || []).map((activity) => [activity.id, activity])),
    [activities.data]
  );

  return {
    ...desk,
    openIds,
    eventsById,
    activitiesById,
    total: total.data ?? null,
    isLoading: desk.isLoading || (openIds.length > 0 && activities.isLoading),
    isError: desk.isError || activities.isError,
    retry: () => { desk.refetch(); activities.refetch(); total.refetch(); },
    refreshCounts: () => total.refetch(),
    /**
     * A scan this desk just saved: bump its count and move the pass to the top
     * of All scans, straight from the save — no request.
     */
    recordScan: (scan) => {
      const bump = (current) => (current == null ? current : current + 1);
      // A list or count kept for one event only takes that event's scans.
      const covers = (scope) => scope === 'all' || scope === scan.eventId;
      if (scan.type === 'CHECKED_IN') {
        queryClient.setQueryData(['checkInTotal', idsKey], bump);
      }
      const countKey = scan.type === 'CHECKED_IN' ? 'checkInRecentIn' : 'checkInRecentOut';
      queryClient.getQueryCache().findAll({ queryKey: [countKey, idsKey] }).forEach(({ queryKey }) => {
        if (covers(queryKey[2])) queryClient.setQueryData(queryKey, bump);
      });
      const cached = queryClient.getQueryCache().findAll({ queryKey: ['checkInRecent', idsKey] });
      // A check-out keeps the check-in time a loaded list already shows for the pass.
      const shownInAt = () => cached
        .flatMap((query) => pageRows(query.state.data))
        .find((item) => item.passId === scan.passId && item.inAt)?.inAt || null;
      const row = {
        passId: scan.passId, name: scan.name, passcode: scan.passcode,
        eventId: scan.eventId, status: scan.type, changedAt: scan.at,
        inAt: scan.type === 'CHECKED_IN' ? scan.at : shownInAt(),
        outAt: scan.type === 'CHECKED_OUT' ? scan.at : null,
      };
      cached.forEach(({ queryKey }) => {
        const [, , scope, filter, search] = queryKey;
        if (!covers(scope)) return;
        // A searched list can't tell whether the pass matches; it reloads when next shown.
        if (search) {
          queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
          return;
        }
        queryClient.setQueryData(queryKey, (current) => (current ? placeScan(current, row, filter) : current));
      });
      // History reloads this event the next time it is opened (no request now).
      queryClient.invalidateQueries({ queryKey: ['checkInHistory', scan.eventId], refetchType: 'none' });
    },
  };
}

/**
 * A saved scan in one cached All scans list: the pass leaves the page it was
 * on and joins the top when its new status belongs to the list. The total
 * grows only when the scan adds someone new to it — a check-in to Checked in,
 * a check-out to Checked out; a check-out only moves its pass up Checked in.
 */
export function placeScan(data, row, filter) {
  const belongs = PASS_STATUSES[filter].includes(row.status);
  const adds = { checked_in: 'CHECKED_IN', checked_out: 'CHECKED_OUT' }[filter] === row.status;
  const delta = adds ? 1 : 0;
  const pages = data.pages.map((page, index) => {
    const rows = page.rows.filter((item) => item.passId !== row.passId);
    return {
      ...page,
      rows: index === 0 && belongs ? [row, ...rows] : rows,
      count: Math.max(0, page.count + delta),
    };
  });
  return { ...data, pages };
}

/**
 * Pages of passes, newest change first — the list behind All scans and the
 * History participants. One read a page; `withTimes` adds one more for that
 * page's check-in and check-out times (none when nobody on it has arrived).
 */
export function usePassPages({
  queryKey, eventIds, statuses, search, registrationIds, withTimes = false, enabled = true,
}) {
  return useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = await fetchPassPage(checkInApi, {
        eventIds, statuses, search, registrationIds, page: pageParam,
      });
      if (!withTimes) return { ...page, page: pageParam };
      // Passes that never arrived have no times, so they aren't asked about.
      const arrived = page.rows.filter((row) => row.status !== 'ISSUED').map((row) => row.passId);
      const times = await fetchPassTimes(checkInApi, arrived);
      return {
        ...page,
        page: pageParam,
        rows: page.rows.map((row) => ({ ...row, inAt: times[row.passId]?.in || null, outAt: times[row.passId]?.out || null })),
      };
    },
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.rows.length, 0);
      return last.rows.length && loaded < last.count ? last.page + 1 : undefined;
    },
    enabled: enabled && eventIds.length > 0,
    staleTime: Infinity,
  });
}

/**
 * The loaded pages as one list. A scan moves its pass to the top, so a page
 * fetched after it can repeat a row already shown; the first copy wins.
 */
export function pageRows(data) {
  const seen = new Set();
  return (data?.pages || []).flatMap((page) => page.rows).filter((row) => {
    if (seen.has(row.passId)) return false;
    seen.add(row.passId);
    return true;
  });
}

/**
 * All scans: passes that have been checked in (Checked in) or out (Checked
 * out), at every open event (`scope` 'all') or at the one event picked.
 */
export function useRecentScans(openIds, { scope, filter, search }, enabled) {
  const idsKey = openIds.join(',');
  const eventIds = scope === 'all' ? openIds : [scope];
  const list = usePassPages({
    queryKey: ['checkInRecent', idsKey, scope, filter, search],
    eventIds,
    statuses: PASS_STATUSES[filter],
    search,
    withTimes: true,
    enabled,
  });
  // Check-ins for one picked event (for all of them the desk's total is used)
  // and check-outs, each one COUNT(*) on its own table.
  const checkedIn = useQuery({
    queryKey: ['checkInRecentIn', idsKey, scope],
    queryFn: () => countChecks(checkInApi, CHECK_IN_TABLE, eventIds),
    enabled: enabled && scope !== 'all',
    staleTime: Infinity,
  });
  const out = useQuery({
    queryKey: ['checkInRecentOut', idsKey, scope],
    queryFn: () => countChecks(checkInApi, CHECK_OUT_TABLE, eventIds),
    enabled: enabled && eventIds.length > 0,
    staleTime: Infinity,
  });
  return { list, checkedIn, out };
}
